
import type { ValidatedAIResponse, ConversationState } from './types/parser';

export const validateAIResponse = (response: any, prevState: ConversationState): ValidatedAIResponse | null => {
  console.log('Raw AI response:', JSON.stringify(response, null, 2));
  console.log('Response type:', typeof response);
  console.log('Previous state:', prevState);
  
  try {
    // If response is a string, try to parse it as JSON
    const parsed = typeof response === 'string' ? JSON.parse(response) : response;
    
    console.log('Parsed structure:', {
      hasType: 'type' in parsed,
      hasAction: 'action' in parsed,
      hasDateTime: 'dateTime' in parsed,
      hasAttendee: 'attendeeName' in parsed,
      contents: parsed
    });
    
    if (!parsed.type || !parsed.action) {
      console.error('Missing required fields in AI response');
      return null;
    }

    if (parsed.type !== 'meeting_request') {
      console.error('Invalid meeting type:', parsed.type);
      return null;
    }

    const validActions = ['schedule', 'reschedule', 'cancel', 'ask_time', 'confirm'];
    if (!validActions.includes(parsed.action)) {
      console.error('Invalid action type:', parsed.action);
      return null;
    }
    
    if (parsed.dateTime) {
      const date = new Date(parsed.dateTime);
      if (isNaN(date.getTime())) {
        console.error('Invalid dateTime format in AI response');
        return null;
      }

      // Get current time in UTC
      const now = new Date();
      const nowUtc = Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate(),
        now.getUTCHours(),
        now.getUTCMinutes(),
        now.getUTCSeconds()
      );

      // Convert meeting date to UTC for comparison
      const meetingUtc = Date.UTC(
        date.getUTCFullYear(),
        date.getUTCMonth(),
        date.getUTCDate(),
        date.getUTCHours(),
        date.getUTCMinutes(),
        date.getUTCSeconds()
      );

      if (meetingUtc < nowUtc) {
        console.error('DateTime is in the past', {
          meeting: new Date(meetingUtc),
          now: new Date(nowUtc)
        });
        return null;
      }

      // Check if the date is too far in the future (e.g., > 1 year)
      const oneYearFromNow = new Date(nowUtc);
      oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
      if (meetingUtc > oneYearFromNow.getTime()) {
        console.error('DateTime is too far in the future');
        return null;
      }
    }

    let isComplete = parsed.isComplete;
    
    if (parsed.action === 'schedule' || parsed.action === 'reschedule') {
      isComplete = !!(parsed.dateTime && (parsed.attendeeName || prevState.attendeeName));
    }

    if (!parsed.attendeeName && prevState.attendeeName) {
      parsed.attendeeName = prevState.attendeeName;
    }

    const duration = parsed.duration || 30;
    if (duration < 15 || duration > 180) {
      console.error('Invalid meeting duration:', duration);
      return null;
    }
    
    return {
      type: parsed.type,
      action: parsed.action,
      dateTime: parsed.dateTime || prevState.dateTime,
      attendeeName: parsed.attendeeName || prevState.attendeeName,
      duration: duration,
      isComplete: isComplete,
      feedback: parsed.feedback || "I'm having trouble understanding. Could you try rephrasing that?"
    };
  } catch (error) {
    console.error('Failed to parse AI response:', error);
    return null;
  }
};

